import { useEffect, useMemo, useState } from "react";
import { NavigateFunction, useLocation, useNavigate } from "react-router-dom";
import {
  classNames,
  cleanString,
  formattedShortTime,
  formattedISODate,
} from "../utils";
import { useGetReportsInProcessQuery } from "../api/ReportsApi";
import {
  useActorInfoQuery,
  useHasOnlyQueueFromActorInfo,
} from "../api/UsersApi";
import Table from "./Table";
import { ReportSummaryApi, ReportSummaryItemApi } from "../api/Reports";
import ReportDetailLink from "./ReportDetailLink";
import ReportNavigationModal from "./ReportNavigationModal";

import Pagination from "../shared-components/Pagination";
import ReportStatus from "./ReportStatus";
import MisconductCancelButton from "./MisconductCancelButton";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import {
  WORKFLOW_MANUAL_SCRAPE_STATUS,
  WORKFLOW_STEP_STATUS_ENUM,
} from "../api/Workflows";

import { LoadedSince } from "../shared-components/LoadedSince";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import {
  ArrowPathRoundedSquareIcon,
  ClipboardIcon,
} from "@heroicons/react/24/outline";
import { genericErrorLoadingMessage, reportFailed } from "./utils";
import TableReportDateWithFormat from "../shared-components/TableReportDateWithFormat";

import ReportStepsModal from "./ReportStepsModal";
import ReactTooltip from "react-tooltip";
import {
  ReportEntry,
  ReportEntryStyle,
  ReportRowItem,
} from "./helper-components/TableModels";
import { useSyncSelectedReportFromList } from "./useSyncSelectedReportFromList";
import { shouldAttachInProcessErrorsOnlyQuery } from "./inProcessErrorsOnly";

const checkPermissionsAndRedirect = (
  canInProcess: boolean,
  canNavAmendment: boolean,
  canComplete: boolean,
  canNavPendingReview: boolean,
  canClientInProgress: boolean,
  canNavReviewed: boolean,
  navigate: NavigateFunction
) => {
  const reports_uri = "/reports/";
  if (canInProcess) {
    return;
  }
  if (canComplete) {
    navigate(reports_uri + "completed", { replace: true });
    return;
  }
  if (canNavPendingReview) {
    navigate(reports_uri + "pending_review", { replace: true });
    return;
  }
  if (canNavReviewed) {
    navigate(reports_uri + "reviewed", { replace: true });
    return;
  }
  if (canClientInProgress) {
    navigate(reports_uri + "in_progress", { replace: true });
    return;
  }
  if (canNavAmendment) {
    navigate(reports_uri + "amendment", { replace: true });
    return;
  }
  navigate("/unavailable", { replace: true });
};

const ReportsInProcess = () => {
  const {
    canCancelMisconduct,
    canManageReports,
    canPostScrape,
    canConfirmProfile,
    canPostRate,
    canInProcess,
    canNavAmendment,
    canComplete,
    canNavPendingReview,
    canClientInProgress,
    canNavReviewed,
    canResumeWorkflow,
  } = usePermissionValidator();

  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [filter, setSearch] = useState(searchParams.get("search") || "");
  const [page, setPage] = useState(1);
  const [selectedReport, setSelectedReport] = useState<ReportSummaryItemApi>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [failedLockWorkflowRuns, setFailedLockWorkflowRuns] = useState<
    Set<string>
  >(new Set());
  const navigate = useNavigate();

  // Redirect after render: react-router ignores navigate() during the first
  // render, which left users without in-process access stuck on /reports.
  useEffect(() => {
    checkPermissionsAndRedirect(
      canInProcess,
      canNavAmendment,
      canComplete,
      canNavPendingReview,
      canClientInProgress,
      canNavReviewed,
      navigate
    );
  }, [
    canInProcess,
    canNavAmendment,
    canComplete,
    canNavPendingReview,
    canClientInProgress,
    canNavReviewed,
    navigate,
  ]);
  const pageSize = 15;
  const SearchName = searchParams.get("search");
  const SearchPage = searchParams.get("page");

  const { data: actorInfo, isLoading: loadingActorInfo } =
    useActorInfoQuery(true);
  const hasOnlyQueue = useHasOnlyQueueFromActorInfo(actorInfo);

  const mayViewInProcessList =
    canInProcess && !loadingActorInfo && !hasOnlyQueue;

  const errorsOnlyQuery = shouldAttachInProcessErrorsOnlyQuery(
    search,
    canResumeWorkflow
  );

  const {
    isLoading: loadingReports,
    data: reportsData,
    error: reportsError,
    refetch: refetchReports,
  } = useGetReportsInProcessQuery(
    page,
    pageSize,
    filter,
    mayViewInProcessList,
    errorsOnlyQuery
  );

  useEffect(() => {
    if (!loadingActorInfo && hasOnlyQueue) {
      navigate("/reports/enter_queue", {
        replace: true,
        state: { stepDone: true },
      });
    }
  }, [loadingActorInfo, hasOnlyQueue, navigate]);

  useEffect(() => {
    setSearch(cleanString(SearchName?.split(" ").join("%20") ?? ""));
    setPage(Number(1));
  }, [SearchName]);

  useEffect(() => {
    setPage(Number(SearchPage || "1"));
  }, [SearchPage]);

  useSyncSelectedReportFromList(selectedReport, setSelectedReport, reportsData);

  const convertReportsInProcessToRows = (
    reportsData: ReportSummaryApi | undefined
  ): ReportRowItem<ReportSummaryItemApi>[] => {
    if (!reportsData) return [];
    const reportRows: ReportRowItem<ReportSummaryItemApi>[] =
      reportsData.report_summaries.map((report: ReportSummaryItemApi) => {
        const data: ReportEntry[] = [
          {
            text: report.full_name,
            style: ReportEntryStyle.COMPONENT,
            component: (
              <ReportDetailLink
                report={report}
                onClick={handleReportClick}
                disabled={
                  !canManageReports ||
                  !checkStatusAndPermission(
                    report.step_name,
                    report.step_payload
                  )
                }
                forceLocked={failedLockWorkflowRuns.has(
                  report.workflow_run_uuid
                )}
                onUnlockSuccess={() =>
                  handleUnlockSuccess(report.workflow_run_uuid)
                }
              ></ReportDetailLink>
            ),
          },
          {
            text: report.workflow_status,
            style: ReportEntryStyle.COMPONENT,
            component: (
              <>
                <ReportStatus
                  stepStatusName={report.step_name}
                  failed={reportFailed(report)}
                  report={report}
                  allowResumeOnClick={true}
                />
                <ReportStepsModal workflowRunUUID={report.workflow_run_uuid} />
              </>
            ),
            customClass: "w-48",
          },
          {
            component: (
              <div className="flex flex-col">
                <span className="border-b-2">{report.blueprint_name}</span>{" "}
                <small className="block">
                  {report.behavior_kit_name ?? "General"}
                </small>
              </div>
            ),
            text:
              report.blueprint_name +
              " " +
              (report.behavior_kit_name ?? "General"),
            style: ReportEntryStyle.COMPONENT,
          },
          {
            text: formattedISODate(report.report_date_added),
            component: report.recur ? (
              <>
                <TableReportDateWithFormat
                  date={report.report_date_added}
                  updated={false}
                  unique_id={report.report_uuid}
                />
                <span
                  data-tip="Employee Screening"
                  data-tooltip-id={`recur-tip-${report.report_uuid}`}
                  className="cursor-pointer"
                >
                  <ArrowPathRoundedSquareIcon className="w-5 text-gray-500" />
                </span>
                <ReactTooltip
                  id={`recur-tip-${report.report_uuid}`}
                  effect="solid"
                />
              </>
            ) : (
              <TableReportDateWithFormat
                date={report.report_date_added}
                updated={false}
              />
            ),
            style: ReportEntryStyle.COMPONENT,
            customClass: "flex-1 whitespace-pre",
          },
          {
            text: formattedShortTime(report.due_date),
            style: ReportEntryStyle.COMPONENT,
            component: (
              <TableReportDateWithFormat
                date={report.due_date}
                updated={report.due_date_updated}
              />
            ),
            customClass: "flex-1 whitespace-pre",
          },
          {
            text: report.report_uuid,
            style: ReportEntryStyle.COMPONENT,
            component: (
              <div className="flex gap-1">
                <ClipboardIcon
                  onClick={() => {
                    navigator.clipboard.writeText(report.report_uuid);
                  }}
                  className="w-4 cursor-pointer"
                ></ClipboardIcon>
                <span
                  onClick={() => handleReportClick(report)}
                  className={classNames(
                    "w-16 h-[30px] truncate",
                    canManageReports &&
                      checkStatusAndPermission(
                        report.step_name,
                        report.step_payload
                      )
                      ? "underline cursor-pointer"
                      : ""
                  )}
                >
                  {report.report_uuid}
                </span>
              </div>
            ),
          },
          {
            text: `${report.client_report_id}`,
            style: ReportEntryStyle.TEXT,
          },
        ];

        if (canCancelMisconduct) {
          data.push({
            text: "Cancel",
            style: ReportEntryStyle.COMPONENT,
            component: (
              <MisconductCancelButton reportUUID={report.report_uuid} />
            ),
          });
        }

        return {
          report: report,
          report_uuid: report.report_uuid,
          isLocked:
            report.is_locked ||
            failedLockWorkflowRuns.has(report.workflow_run_uuid),
          data: data,
        };
      });
    return reportRows;
  };

  function checkStatusAndPermission(
    step_name: string | null,
    step_payload: any
  ) {
    return (
      (step_name === WORKFLOW_STEP_STATUS_ENUM.PostsManualRate &&
        canPostRate) ||
      (step_name === WORKFLOW_STEP_STATUS_ENUM.PostsManualScrape &&
        step_payload.MANUAL_SCRAPE_READY ===
          WORKFLOW_MANUAL_SCRAPE_STATUS.IN_PROGRESS &&
        canPostScrape) ||
      (step_name === WORKFLOW_STEP_STATUS_ENUM.ProfilesManualRate &&
        canConfirmProfile) ||
      (step_name === WORKFLOW_STEP_STATUS_ENUM.WebContentReview && canPostRate) // TODO: define permission for webcontent
    );
  }

  function handleReportClick(report: ReportSummaryItemApi) {
    if (!canManageReports) return;
    if (!checkStatusAndPermission(report.step_name, report.step_payload)) {
      return;
    }
    setSelectedReport(report);
    if (report.step_name) toggleOpen(true);
  }

  function toggleOpen(isOpen: boolean) {
    setIsModalOpen(isOpen);
  }

  function handleLockFailed(workflowRunUUID: string) {
    setFailedLockWorkflowRuns((prev) => new Set(prev).add(workflowRunUUID));
  }

  function handleUnlockSuccess(workflowRunUUID: string) {
    setFailedLockWorkflowRuns((prev) => {
      const newSet = new Set(prev);
      newSet.delete(workflowRunUUID);
      return newSet;
    });
  }

  function clearCache() {
    refetchReports();
  }

  if (loadingActorInfo || hasOnlyQueue) {
    return (
      <div className="flex w-full items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (reportsError) {
    return (
      <div className="p-4 rounded-xl bg-red-300 text-red-900">
        {genericErrorLoadingMessage}
      </div>
    );
  }

  function buildHeader() {
    const tableHeader = [
      "Name / Company",
      { text: "Status", customClass: "w-44" },
      "Check Type / Behavior Kit",
      "Date Added",
      "Due Date",
      "Report ID",
      "Client Reference ID",
    ];
    if (canCancelMisconduct) {
      tableHeader.push("Cancel Report");
    }
    return tableHeader;
  }

  const showLastUpdated = Boolean(reportsData?.timestamp);

  return (
    <>
      {showLastUpdated && (
        <div className="absolute right-2 -top-[30px] z-10 flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
          <LoadedSince
            reportData={reportsData}
            onClear={clearCache}
            className="flex shrink-0 items-center gap-2 text-sm text-gray-600"
          />
        </div>
      )}
      <Table
        isLoading={loadingReports}
        header={buildHeader()}
        data={convertReportsInProcessToRows(reportsData)}
      />

      {selectedReport && (
        <ReportNavigationModal
          report={selectedReport}
          isOpen={isModalOpen}
          toggleOpen={toggleOpen}
          onLockFailed={handleLockFailed}
          forceLocked={failedLockWorkflowRuns.has(
            selectedReport.workflow_run_uuid
          )}
        />
      )}
      {!loadingReports && reportsData && (
        <Pagination
          pageSize={pageSize}
          currentPage={page}
          totalCount={reportsData.total_rows}
          search={filter}
          params={errorsOnlyQuery ? { errors_only: "1" } : undefined}
        />
      )}
    </>
  );
};

export default ReportsInProcess;
